import { Box, Skeleton, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion, Switch } from '@europrocurement/flexy-components';
import { useFormaliteService, type DossierOpenSearch } from '@europrocurement/l2d-domain';
import { type ApiDossiersIdNotesGet200ResponseNotesInner } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { markDownToPlainText } from '@europrocurement/l2d-utils';
import { GenericModalMessageFormContext } from '@europrocurement/flexy-components/types/GenericModalMessageTypes';
import NotesSelectorForm from '@europrocurement/l2d-modules/modules/Notes/components/NotesSelectorsForm/NotesSelectorsForm';
import {
    GenericModalMailFormContext,
    MailContactType,
} from '@europrocurement/flexy-components/types/GenericModalMailType';
import GenericModalMailContent from '../../../../MailForm/components/GenericModalMailContent/GenericModalMailContent';
import { type DisplayedDocument } from '../../../providers/types';
import DocumentsSelectorsForm from '../DocumentsSelectorsForm/DocumentsSelectorsForm';
import { Tiers } from '../../../hooks/useDossierPrescripteur';
import GenericModalMessageContent from '../../../../MessageNotificationForm/component/GenericModalMessageContent';

type dossierWithNotes = {
    dossier: DossierOpenSearch;
    notes: ApiDossiersIdNotesGet200ResponseNotesInner[];
};

type AdditionnalCollectionRequestFormProps = {
    missingDocs: DisplayedDocument[];
    dosDocs: DisplayedDocument[];
    dossiers: DossierOpenSearch[];
    dossier: DossierOpenSearch;
    prescriber: Tiers;
    onSuccess?: () => void;
};

const AdditionnalCollectionRequestForm: React.FunctionComponent<AdditionnalCollectionRequestFormProps> =
    function ({ missingDocs, dosDocs, dossiers, dossier, prescriber, onSuccess }) {
        const [documentsIds, setDocumentsIds] = useState<number[]>([]);
        const [selectedDocuments, setSelectedDocuments] = useState<DisplayedDocument[]>([]);
        const [selectedNotes, setSelectedNotes] = useState<
            ApiDossiersIdNotesGet200ResponseNotesInner[]
        >([]);
        const [preSelectedNotesIds, setPreSelectedNotesIds] = useState<number[]>([]);
        const [dossiersWithNotes, setDossiersWithNotes] = useState<dossierWithNotes[]>([]);
        // GetFormContext from child
        const [childFormContext, setChildFormContext] = useState<
            GenericModalMailFormContext | GenericModalMessageFormContext
        >();

        // Blocs text
        const [textContentDocs, setTextContentDocs] = useState<string>('');
        const [textContentNotes, setTextContentNotes] = useState<string>('');
        const [textContent, setTextContent] = useState('');
        const [currentBody, setCurrentBody] = useState<string>('');
        const [showMessageForm, setShowMessageForm] = useState<boolean>(false);

        const { dossierApi } = useFormaliteService();

        useEffect(() => {
            if (missingDocs) {
                setDocumentsIds(missingDocs.map((doc) => doc.documentTypeId as number));
            }
        }, [missingDocs]);

        const loadDossiersNotes = async () => {
            const results = await Promise.all(
                dossiers.map(async (doss) => {
                    if (!doss.id) return null;

                    const { data } = await dossierApi.apiDossiersIdNotesGet({
                        id: doss.id,
                        memoClient: true,
                    });

                    if (!data?.notes?.length) return null;

                    return {
                        dossier: doss,
                        notes: data.notes as ApiDossiersIdNotesGet200ResponseNotesInner[],
                    };
                }),
            );

            // Remove null data if existing
            const dossierWithNotesResponse = results.filter(
                (res) => res && res.notes && res.notes.length > 0,
            ) as dossierWithNotes[];

            // Update State dossier with Notes
            setDossiersWithNotes(dossierWithNotesResponse);

            // Extract notes ids
            const notesIds: number[] = [];
            dossierWithNotesResponse.forEach((dossNotes) =>
                dossNotes.notes.forEach((note) => notesIds.push(note.id as number)),
            );
            setPreSelectedNotesIds(notesIds);
        };
        useEffect(() => {
            loadDossiersNotes();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [dossiers]);

        const getFormContext = useCallback(
            (fromCtxt: GenericModalMailFormContext | GenericModalMessageFormContext) => {
                setChildFormContext(fromCtxt);
            },
            [],
        );

        // Mulitple use effect to update differents parts of mail and textArea
        //
        // Update text Content doc
        useEffect(() => {
            let textContentDoc = '';
            if (selectedDocuments.length > 0) {
                textContentDoc += `\n\nDocuments manquants :`;
                selectedDocuments.forEach((doc) => {
                    textContentDoc += `\n- ${doc.label}`;
                });
            }
            setTextContentDocs(textContentDoc);
        }, [selectedDocuments]);

        // Update text Content notes
        useEffect(() => {
            let textContentnote = '';
            if (selectedNotes.length > 0) {
                textContentnote += `\n\nInformations manquantes :`;

                selectedNotes.forEach((note) => {
                    textContentnote += `\n ${note.content}`;
                });
            }
            setTextContentNotes(textContentnote);
        }, [selectedNotes]);

        // Update composed textContent
        useEffect(() => {
            let txtContent = `Bonjour,\n\nAfin de compléter votre dossier, nous vous invitons à nous communiquer les éléments suivants :`;
            txtContent += textContentNotes;
            txtContent += textContentDocs;
            txtContent += `\n\nMerci d'avance pour votre retour.\n\nBien cordialement,\nL'équipe Formalités L2D`;
            setTextContent(() => txtContent);
        }, [textContentDocs, textContentNotes]);

        // Update form context textArea
        useEffect(() => {
            if (!childFormContext) return;

            const plainText = markDownToPlainText(currentBody);

            if ('setMailBody' in childFormContext) {
                childFormContext.setMailBody(plainText);
            } else {
                childFormContext.setMessageBody(plainText);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [currentBody]);

        useEffect(() => {
            setCurrentBody(textContent);
        }, [textContent]);

        const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            if (childFormContext) {
                if ('getMailBody' in childFormContext) {
                    setCurrentBody(childFormContext.getMailBody() || '');
                } else if ('getMessageBody' in childFormContext) {
                    setCurrentBody(childFormContext.getMessageBody() || '');
                }
                setShowMessageForm(checked);
            }
        };

        const contactEmailAttitreOuPrincipal = useMemo(() => {
            if (prescriber && prescriber.contacts) {
                let contact = prescriber.contacts.find((ctct) => ctct.id === dossier.contactId);
                if (!contact || !contact.emailsContact || contact.emailsContact.length === 0) {
                    contact = prescriber.contacts.find((ctct) => ctct.principal === true);
                }

                if (contact) {
                    const emailContact = contact.emailsContact.find(
                        (mailContact) => mailContact.defaut,
                    );
                    if (emailContact && emailContact.email) {
                        return {
                            emailAddress: emailContact.email,
                            firstName: contact.prenomContact,
                            lastName: contact.nomContact,
                        } as MailContactType;
                    }
                }
            }
            return undefined;
        }, [dossier.contactId, prescriber]);

        // Add this before the `return (` (somewhere after effects and handlers)
        const messageArea = (() => {
            if (!textContent || !prescriber) {
                return <Skeleton sx={{ minHeight: '700px', marginTop: '-100px' }} />;
            }

            if (showMessageForm) {
                return (
                    <GenericModalMessageContent
                        body={currentBody}
                        getFormContext={getFormContext}
                        entity={dossier}
                    />
                );
            }

            return (
                <GenericModalMailContent
                    to={contactEmailAttitreOuPrincipal ? [contactEmailAttitreOuPrincipal] : []}
                    showTo
                    subject={`Legal2digital - ${dossier.collectionNumero} - Demande d’informations complémentaires`}
                    showSubject
                    body={currentBody}
                    searchParamsTiersIds={[prescriber.id as number]}
                    showBody
                    showIaButtons
                    attachments={[]}
                    showAttachments
                    getFormContext={getFormContext}
                    fieldsOrdering={[
                        'recipients',
                        'subject',
                        'iaButtons',
                        'mailBody',
                        'buttonsFooter',
                        'attachmentsUploader',
                    ]}
                    autoBalisesForMail
                    onSuccess={onSuccess}
                    driveDocuments={dosDocs}
                    showDriveDocButton
                />
            );
        })();

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', minHeight: '55vh' }}>
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        width: '50%',
                        padding: '16px',
                        maxHeight: '75vh',
                        overflow: 'auto',
                    }}
                >
                    <Box>
                        <Typography variant="l2dh6">Demander des informations</Typography>
                        {dossier.collectionNumero && (
                            <Typography
                                variant="l2dcaption"
                                sx={{ color: 'text.gris70' }}
                            >
                                Commande #{dossier.collectionNumero}
                            </Typography>
                        )}
                    </Box>

                    {dossiersWithNotes && dossiersWithNotes.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <Typography variant="l2dsubtitles">Notes</Typography>

                            {dossiersWithNotes.map((dossWithNotes) => (
                                <Accordion
                                    key={`Notes_${dossWithNotes.dossier.numero}`}
                                    title={<Typography>{dossWithNotes.dossier.numero} </Typography>}
                                    expanded
                                >
                                    <NotesSelectorForm
                                        notes={dossWithNotes.notes}
                                        setSelectedNotes={setSelectedNotes}
                                        preSelectedNotesIds={preSelectedNotesIds}
                                    />
                                </Accordion>
                            ))}
                        </Box>
                    )}

                    {missingDocs.length > 0 && documentsIds && (
                        <Box>
                            <Typography variant="l2dsubtitles">Documents manquants</Typography>

                            <DocumentsSelectorsForm
                                documents={missingDocs}
                                preSelectedDocumentsIds={documentsIds}
                                setSelectedDocuments={setSelectedDocuments}
                            />
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        width: '50%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        padding: '16px',
                        maxHeight: '75vh',
                        overflow: 'auto',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="l2dh6">Message généré</Typography>
                        <Box
                            sx={{
                                width: '50%',
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '24px',
                                padding: '16px',
                                maxHeight: '75vh',
                                overflow: 'auto',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                variant="l2dbodys"
                                fontWeight={500}
                            >
                                Envoyer par message ?
                            </Typography>
                            <Switch
                                size="small"
                                aria-label="switch"
                                value={showMessageForm}
                                onChange={(e, checked: boolean) => {
                                    handleSwitchChange(e, checked);
                                }}
                            />
                        </Box>
                    </Box>
                    {messageArea}
                </Box>
            </Box>
        );
    };

export default AdditionnalCollectionRequestForm;
